import { createCrudStore } from "./crudStore";
import apiTamboUrl, { apiService } from "@/services/api";

const dietasStore = createCrudStore("dietas");

export default {
  ...dietasStore,

  state: {
    ...dietasStore.state,
    aplicaciones: [],
    registros: [],
    equivalencias: [],
    registrosComida: [],
    balanceadosCampo: [],
    balanceadosCompra: [],
    comidasDia: [],
    comparacionProduccion: null,
    estadoCorporalLote: null,
    promediosCorporales: null,
    contextoDestino: null,
    suficienciaStock: [],
    historialPastoreo: {},
    costoPastoreo: null,
  },

  getters: {
    ...dietasStore.getters,
    aplicaciones: (state) => state.aplicaciones || [],
    registros: (state) => state.registros || [],
    equivalencias: (state) => state.equivalencias || [],
    registrosComida: (state) => state.registrosComida || [],
    balanceadosCampo: (state) => state.balanceadosCampo || [],
    balanceadosCompra: (state) => state.balanceadosCompra || [],
    comidasDia: (state) => state.comidasDia || [],
    comparacionProduccion: (state) => state.comparacionProduccion,
    estadoCorporalLote: (state) => state.estadoCorporalLote,
    promediosCorporales: (state) => state.promediosCorporales,
    contextoDestino: (state) => state.contextoDestino,
    suficienciaStock: (state) => state.suficienciaStock || [],
    historialPastoreo: (state) => state.historialPastoreo || {},
    costoPastoreo: (state) => state.costoPastoreo,
  },

  mutations: {
    ...dietasStore.mutations,
    setAplicaciones(state, data) {
      state.aplicaciones = data;
    },
    setRegistros(state, data) {
      state.registros = data;
    },
    setEquivalencias(state, data) {
      state.equivalencias = data;
    },
    setRegistrosComida(state, data) {
      state.registrosComida = data;
    },
    setBalanceadosCampo(state, data) {
      state.balanceadosCampo = data;
    },
    setBalanceadosCompra(state, data) {
      state.balanceadosCompra = data;
    },
    setComidasDia(state, data) {
      state.comidasDia = data;
    },
    setComparacionProduccion(state, data) {
      state.comparacionProduccion = data;
    },
    setEstadoCorporalLote(state, data) {
      state.estadoCorporalLote = data;
    },
    setPromediosCorporales(state, data) {
      state.promediosCorporales = data;
    },
    setContextoDestino(state, data) {
      state.contextoDestino = data;
    },
    setSuficienciaStock(state, data) {
      state.suficienciaStock = data;
    },
    setHistorialPastoreo(state, { aplicacionId, data }) {
      state.historialPastoreo = {
        ...state.historialPastoreo,
        [aplicacionId]: data,
      };
    },
    setCostoPastoreo(state, data) {
      state.costoPastoreo = data;
    },
    removeDietaItem(state, id) {
      state.dietas = (state.dietas || []).filter(
        (item) => item.dieta_id !== id && item.id !== id
      );
    },
  },

  actions: {
    ...dietasStore.actions,
    getDietas: dietasStore.actions.getAllStates,
    postDieta: dietasStore.actions.create,
    patchDieta: dietasStore.actions.update,
    async deleteDieta({ dispatch, commit }, dieta_id) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiTamboUrl.delete(`/dietas/${dieta_id}`);
        commit("removeDietaItem", dieta_id);
        try {
          await dispatch("getDietas");
          await dispatch("getAplicaciones");
        } catch (e) {
          console.warn(
            "Error refrescando dietas/aplicaciones tras eliminar:",
            e
          );
        }
        return response;
      } catch (error) {
        console.error("Error eliminando dieta:", error);
        commit("setError", "Error al eliminar la dieta");
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    // ==================== BALANCEADOS DISPONIBLES ====================

    async getBalanceadosCampo({ dispatch, commit }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom("dietas/disponibles/campo"),
        onSuccessCallback: (_, data) => commit("setBalanceadosCampo", data),
        errorMessage: "Error obteniendo balanceados de campo",
      });
    },

    async getBalanceadosCompra({ dispatch, commit }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom("dietas/disponibles/compra"),
        onSuccessCallback: (_, data) => commit("setBalanceadosCompra", data),
        errorMessage: "Error obteniendo balanceados de compra",
      });
    },

    // ==================== APLICACIONES ====================

    async getAplicaciones({ commit }) {
      try {
        const response = await apiTamboUrl.get("/dietas/aplicaciones");
        commit("setAplicaciones", response.data);
        return response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          commit("setAplicaciones", []);
          return [];
        }
        console.error("Error obteniendo aplicaciones de dieta:", error);
        throw error;
      }
    },

    async postAplicarDieta({ dispatch, commit }, payload) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.postCustom("dietas/aplicar", payload);
        try {
          await dispatch("getDietas");
          await dispatch("getAplicaciones");
        } catch (e) {
          console.warn(
            "Error refrescando dietas/aplicaciones tras aplicar:",
            e
          );
        }
        return response;
      } catch (error) {
        console.error("Error aplicando dieta:", error);
        commit("setError", "Error al aplicar la dieta");
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async patchAplicacion({ dispatch }, { aplicacion_id, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(
            `dietas/aplicaciones/${aplicacion_id}`,
            payload
          ),
        onSuccessCallback: async () => {
          await dispatch("getAplicaciones");
        },
        errorMessage: "Error modificando aplicación de dieta",
      });
    },

    async deleteAplicacion({ dispatch }, aplicacion_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`dietas/aplicaciones/${aplicacion_id}`),
        onSuccessCallback: async () => {
          await dispatch("getAplicaciones");
          await dispatch("getAllStates");
        },
        errorMessage: "Error eliminando aplicación de dieta",
      });
    },

    // ==================== COMIDAS ====================

    async getComidas({ dispatch }, dieta_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`dietas/${dieta_id}/comidas`),
        errorMessage: "Error obteniendo comidas de la dieta",
      });
    },

    async postComida({ dispatch }, { dieta_id, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`dietas/${dieta_id}/comidas`, payload),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error agregando comida a la dieta",
      });
    },

    async patchComida({ dispatch }, { comida_id, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(`dietas/comidas/${comida_id}`, payload),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error modificando comida",
      });
    },

    async deleteComida({ dispatch }, comida_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`dietas/comidas/${comida_id}`),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error eliminando comida",
      });
    },

    // ==================== INGREDIENTES DE COMIDAS ====================

    async getIngredientesComida({ dispatch }, comida_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`dietas/comidas/${comida_id}/ingredientes`),
        errorMessage: "Error obteniendo ingredientes de la comida",
      });
    },

    async postIngredienteComida({ dispatch }, { comida_id, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `dietas/comidas/${comida_id}/ingredientes`,
            payload
          ),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error agregando ingrediente a la comida",
      });
    },

    async patchIngredienteComida({ dispatch }, { ingrediente_id, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(
            `dietas/comidas/ingredientes/${ingrediente_id}`,
            payload
          ),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error modificando ingrediente de comida",
      });
    },

    async deleteIngredienteComida({ dispatch }, ingrediente_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(
            `dietas/comidas/ingredientes/${ingrediente_id}`
          ),
        onSuccessCallback: async () => {
          await dispatch("getAllStates");
        },
        errorMessage: "Error eliminando ingrediente de comida",
      });
    },

    // ==================== COMIDAS DEL DÍA (por aplicación) ====================

    async getComidasDia({ dispatch, commit }, { aplicacion_id, fecha }) {
      const url = fecha
        ? `dietas/aplicaciones/${aplicacion_id}/comidas-dia?fecha=${fecha}`
        : `dietas/aplicaciones/${aplicacion_id}/comidas-dia`;
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom(url),
        onSuccessCallback: (_, data) => commit("setComidasDia", data),
        errorMessage: "Error obteniendo comidas del día",
      });
    },

    // ==================== REGISTROS DE CONSUMO POR COMIDA ====================

    async getRegistrosComida({ commit }, params = {}) {
      let url = "/dietas/registros/comida";
      const queryParts = [];
      if (params.aplicacion_id)
        queryParts.push(`aplicacion_id=${params.aplicacion_id}`);
      if (params.fecha_desde)
        queryParts.push(`fecha_desde=${params.fecha_desde}`);
      if (params.fecha_hasta)
        queryParts.push(`fecha_hasta=${params.fecha_hasta}`);
      if (queryParts.length) url += "?" + queryParts.join("&");

      try {
        const response = await apiTamboUrl.get(url);
        commit("setRegistrosComida", response.data);
        return response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          commit("setRegistrosComida", []);
          return [];
        }
        console.error("Error obteniendo registros de consumo:", error);
        throw error;
      }
    },

    async postRegistroComida({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("dietas/registros/comida", payload),
        onSuccessCallback: async () => {
          await dispatch("getRegistrosComida");
        },
        errorMessage: "Error registrando consumo de comida",
      });
    },

    async putRegistroComida({ dispatch }, payload) {
      const registroId =
        payload.id || payload.registroId || payload.registro_id;
      const data = { ...payload };
      delete data.id;
      delete data.registroId;
      delete data.registro_id;
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.putCustom(`dietas/registros/comida/${registroId}`, data),
        onSuccessCallback: async () => {
          await dispatch("getRegistrosComida");
        },
        errorMessage: "Error actualizando registro de consumo de comida",
      });
    },

    async patchRegistroComida({ dispatch }, payload) {
      const registroId =
        payload.id || payload.registroId || payload.registro_id;
      const data = { ...payload };
      delete data.id;
      delete data.registroId;
      delete data.registro_id;
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(`dietas/registros/comida/${registroId}`, data),
        onSuccessCallback: async () => {
          await dispatch("getRegistrosComida");
        },
        errorMessage: "Error actualizando registro de consumo de comida",
      });
    },

    async updateRegistroComida({ dispatch }, payload) {
      return dispatch("putRegistroComida", payload);
    },

    async getRegistrosDia({ dispatch }, fecha) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`dietas/registros/dia/${fecha}`),
        errorMessage: "Error obteniendo registros del día",
      });
    },

    // ==================== REGISTROS ANTIGUOS (compatibilidad) ====================

    async getRegistros({ dispatch, commit }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom("dietas/registros/"),
        onSuccessCallback: (_, data) => commit("setRegistros", data),
        errorMessage: "Error obteniendo registros de consumo",
      });
    },

    async postRegistro({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("dietas/registros/", payload),
        onSuccessCallback: async () => {
          await dispatch("getRegistros");
        },
        errorMessage: "Error creando registro de consumo",
      });
    },

    async deleteRegistro({ dispatch }, registro_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`dietas/registros/${registro_id}`),
        onSuccessCallback: async () => {
          await dispatch("getRegistros");
        },
        errorMessage: "Error eliminando registro de consumo",
      });
    },

    // ==================== EQUIVALENCIAS ====================

    async getEquivalencias({ dispatch, commit }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom("dietas/equivalencias/"),
        onSuccessCallback: (_, data) => commit("setEquivalencias", data),
        errorMessage: "Error obteniendo equivalencias de unidad",
      });
    },

    async postEquivalencia({ dispatch }, payload) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("dietas/equivalencias/", payload),
        onSuccessCallback: async () => {
          await dispatch("getEquivalencias");
        },
        errorMessage: "Error creando equivalencia de unidad",
      });
    },

    async deleteEquivalencia({ dispatch }, equivalencia_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`dietas/equivalencias/${equivalencia_id}`),
        onSuccessCallback: async () => {
          await dispatch("getEquivalencias");
        },
        errorMessage: "Error eliminando equivalencia de unidad",
      });
    },

    // ==================== COMPARACIÓN DE PRODUCCIÓN ====================

    async getComparacionProduccion({ commit, dispatch }, { aplicacion_id }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `dietas/aplicaciones/${aplicacion_id}/comparar-produccion`
          ),
        onSuccessCallback: (data) => {
          commit("setComparacionProduccion", data);
        },
        errorMessage: "Error obteniendo comparación de producción",
      });
    },

    // ==================== COMPARACIÓN DE CONSUMO (mensual/estacional) ====================

    async getComparacionConsumo({ dispatch }, params) {
      let url = "dietas/consumo/comparacion?";
      const queryParts = [];
      if (params.modo) queryParts.push(`modo=${params.modo}`);
      if (params.anio_a != null) queryParts.push(`anio_a=${params.anio_a}`);
      if (params.mes_a != null) queryParts.push(`mes_a=${params.mes_a}`);
      if (params.anio_b != null) queryParts.push(`anio_b=${params.anio_b}`);
      if (params.mes_b != null) queryParts.push(`mes_b=${params.mes_b}`);
      if (params.estacion_a) queryParts.push(`estacion_a=${params.estacion_a}`);
      if (params.estacion_b) queryParts.push(`estacion_b=${params.estacion_b}`);
      url += queryParts.join("&");

      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom(url),
        errorMessage: "Error obteniendo comparación de consumo",
      });
    },

    // ==================== MIXER DE COMIDA ====================

    async getMixerComida({ dispatch }, comida_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`dietas/comidas/${comida_id}/mixer`),
        errorMessage: "Error obteniendo detalle del mixer",
      });
    },

    // ==================== ESTADO CORPORAL POR LOTE ====================

    async getEstadoCorporalLote({ dispatch, commit }, lote_id) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `dietas/estado-corporal-por-lote?lote_id=${lote_id}`
          ),
        onSuccessCallback: (_, data) => commit("setEstadoCorporalLote", data),
        errorMessage: "Error obteniendo estado corporal del lote",
      });
    },

    // ==================== COSTO TEÓRICO DE DIETA ====================

    async getCostoTeoricoDieta(
      { dispatch },
      { dieta_id, dias_estimados, lote_id, corral_id }
    ) {
      // cantidad_vacas=1 → el backend lo auto-detecta desde el lote/corral
      let url = `dietas/${dieta_id}/costo-teorico?cantidad_vacas=1&dias_estimados=${
        dias_estimados || 30
      }`;

      if (lote_id) {
        url += `&lote_id=${lote_id}`;
      }
      if (corral_id) {
        url += `&corral_id=${corral_id}`;
      }

      return dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom(url),
        errorMessage: "Error calculando costo teórico de la dieta",
      });
    },

    // ==================== PROMEDIOS CORPORALES ====================

    async fetchPromediosCorporales({ commit }, { tamboId, loteId } = {}) {
      let url = "dietas/estado-corporal-por-lote";
      const query = [];
      if (loteId) query.push(`lote_id=${loteId}`);
      if (tamboId) query.push(`tambo_id=${tamboId}`);
      if (query.length) url += `?${query.join("&")}`;

      try {
        const resp = await apiService.getCustom(url);
        const data = resp?.data ?? resp;
        if (data?.lotes && data.lotes.length > 0) {
          commit("setPromediosCorporales", data);
          return data;
        }
      } catch (e) {
        console.warn(
          "Error en dietas/estado-corporal-por-lote, intentando fallback:",
          e
        );
      }

      try {
        const fallbackResp = await apiService.getCustom(
          "grilla_vaca/resumen?limite=1000&pagina=1"
        );
        const fallbackData = fallbackResp?.data ?? fallbackResp;
        commit("setPromediosCorporales", fallbackData);
        return fallbackData;
      } catch (error) {
        console.error(
          "Error al obtener promedios corporales (fallback):",
          error
        );
        throw error;
      }
    },

    // ==================== CONTEXTO DESTINO ====================

    async fetchContextoDestino({ commit }, payload = {}) {
      let q = "";
      if (typeof payload === "string") {
        q = payload;
      } else if (payload.queryParam) {
        q = payload.queryParam;
      } else if (payload.lote_id || payload.loteId) {
        q = `lote_id=${payload.lote_id || payload.loteId}`;
      } else if (payload.corral_id || payload.corralId) {
        q = `corral_id=${payload.corral_id || payload.corralId}`;
      }

      const url = q
        ? `dietas/contexto-destino?${q}`
        : "dietas/contexto-destino";
      try {
        const response = await apiService.getCustom(url);
        const data = response?.data ?? response;
        commit("setContextoDestino", data);
        return data;
      } catch (error) {
        console.error("Error al obtener contexto de destino:", error);
        throw error;
      }
    },

    // ==================== SUFICIENCIA STOCK ====================

    async fetchSuficienciaStock({ commit }) {
      try {
        const response = await apiTamboUrl.get("/dietas/suficiencia-stock");
        const data = response.data || [];
        commit("setSuficienciaStock", data);
        return data;
      } catch (error) {
        console.error("Error al obtener suficiencia de stock:", error);
        throw error;
      }
    },

    // ==================== HISTORIAL PASTOREO ====================

    async fetchHistorialPastoreo({ commit }, aplicacionId) {
      try {
        const response = await apiTamboUrl.get(
          `/dietas/aplicaciones/${aplicacionId}/historial-pastoreo`
        );
        const data = response.data || [];
        commit("setHistorialPastoreo", { aplicacionId, data });
        return data;
      } catch (error) {
        console.error("Error al obtener historial de pastoreo:", error);
        throw error;
      }
    },

    // ==================== COSTO PASTOREO ====================

    async fetchCostoPastoreo({ commit }, pasturaId) {
      try {
        const response = await apiTamboUrl.get(
          `/pasturas/${pasturaId}/costo-pastoreo`
        );
        commit("setCostoPastoreo", response.data);
        return response.data;
      } catch (error) {
        console.error("Error al obtener costo de pastoreo:", error);
        throw error;
      }
    },

    // ==================== ELIMINAR APLICACION PASTOREO ====================

    async deleteAplicacionPastoreo({ commit }, id) {
      try {
        const response = await apiTamboUrl.delete(
          `/pasturas/carga-animal/${id}`
        );
        return response.data ?? response;
      } catch (error) {
        console.error("Error al eliminar asignación de pastoreo:", error);
        throw error;
      }
    },
  },
};
